"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Save, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useEmailAgentConfig } from "@/hooks/agents/email";
import {
  DEFAULT_ANALYTICS_CONFIG,
  PERIOD_OPTIONS,
} from "../analytics/constants";
import type { AnalyticsConfig, AnalyticsPeriod } from "../analytics/types";

interface AnalyticsSettingsProps {
  agentId: string;
}

export function AnalyticsSettings({ agentId }: AnalyticsSettingsProps) {
  const { config, isLoading: isLoadingConfig, loadConfig, saveConfig, isSaving } =
    useEmailAgentConfig(agentId);

  const [formState, setFormState] = useState<AnalyticsConfig>(DEFAULT_ANALYTICS_CONFIG);

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Sync config into form state
  useEffect(() => {
    if (config?.analytics) {
      const a = config.analytics;
      setFormState({
        ...DEFAULT_ANALYTICS_CONFIG,
        ...(a.default_period != null && { default_period: a.default_period as AnalyticsPeriod }),
        ...(a.handover_definition != null && { handover_definition: a.handover_definition as AnalyticsConfig["handover_definition"] }),
        ...(a.silent_close_days != null && { silent_close_days: a.silent_close_days }),
        time_constants: {
          ...DEFAULT_ANALYTICS_CONFIG.time_constants,
          ...(a.time_constants ?? {}),
        },
        sentiment_thresholds: {
          ...DEFAULT_ANALYTICS_CONFIG.sentiment_thresholds,
          ...(a.sentiment_thresholds ?? {}),
        },
        response_time: {
          ...DEFAULT_ANALYTICS_CONFIG.response_time,
          ...(a.response_time ?? {}),
        },
        ...(a.flow_groups != null && { flow_groups: a.flow_groups }),
      });
    }
  }, [config]);

  const handleSave = async () => {
    try {
      await saveConfig({ analytics: formState });
      toast.success("Analytics settings saved");
    } catch {
      toast.error("Failed to save settings");
    }
  };

  const handleReset = () => {
    setFormState(DEFAULT_ANALYTICS_CONFIG);
  };

  const updateTimeConstant = (key: keyof AnalyticsConfig["time_constants"], value: string) => {
    const num = parseFloat(value);
    if (!isNaN(num) && num >= 0) {
      setFormState((prev) => ({
        ...prev,
        time_constants: { ...prev.time_constants, [key]: num },
      }));
    }
  };

  const updateSentimentThreshold = (key: keyof AnalyticsConfig["sentiment_thresholds"], value: string) => {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= 0 && num <= 10) {
      setFormState((prev) => ({
        ...prev,
        sentiment_thresholds: { ...prev.sentiment_thresholds, [key]: num },
      }));
    }
  };

  if (isLoadingConfig) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* General */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">General</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Default Period</Label>
              <Select
                value={formState.default_period}
                onValueChange={(v) =>
                  setFormState((prev) => ({ ...prev, default_period: v as AnalyticsPeriod }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Escalation Definition</Label>
              <Select
                value={formState.handover_definition}
                onValueChange={(v) =>
                  setFormState((prev) => ({
                    ...prev,
                    handover_definition: v as AnalyticsConfig["handover_definition"],
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="escalated_only">Escalated Only</SelectItem>
                  <SelectItem value="escalated_and_forwarded">
                    Escalated + Forwarded
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Silent Close (days)</Label>
              <Input
                type="number"
                min={1}
                max={90}
                value={formState.silent_close_days}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v > 0) {
                    setFormState((prev) => ({ ...prev, silent_close_days: v }));
                  }
                }}
              />
              <p className="text-xs text-muted-foreground">
                Days of silence before a thread is considered closed
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Response Time */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Response Time</CardTitle>
          <p className="text-xs text-muted-foreground">
            Configure which responses count toward response time metrics
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-6">
            <div className="space-y-1.5">
              <Label>Count AI agent replies</Label>
              <p className="text-xs text-muted-foreground">
                When on, auto-replies sent by the agent count as a response.
                When off, emails handled solely by the agent are excluded from stats
                so you see only human response time.
              </p>
            </div>
            <Switch
              checked={formState.response_time.include_agent_replies}
              onCheckedChange={(checked) =>
                setFormState((prev) => ({
                  ...prev,
                  response_time: { ...prev.response_time, include_agent_replies: checked },
                }))
              }
            />
          </div>
          <div className="flex items-center justify-between gap-6">
            <div className="space-y-1.5">
              <Label>Count forwarded as responded</Label>
              <p className="text-xs text-muted-foreground">
                When on, emails forwarded to another inbox count as responded.
                When off, they are excluded since we can&apos;t track the actual reply.
              </p>
            </div>
            <Switch
              checked={formState.response_time.include_forwarded}
              onCheckedChange={(checked) =>
                setFormState((prev) => ({
                  ...prev,
                  response_time: { ...prev.response_time, include_forwarded: checked },
                }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label>SLA threshold (hours)</Label>
            <Input
              type="number"
              min={1}
              max={720}
              value={formState.response_time.sla_threshold_hours}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!isNaN(v) && v >= 1) {
                  setFormState((prev) => ({
                    ...prev,
                    response_time: { ...prev.response_time, sla_threshold_hours: v },
                  }));
                }
              }}
            />
            <p className="text-xs text-muted-foreground">
              Emails exceeding this threshold are flagged as over-SLA
            </p>
          </div>
          <div className="space-y-2">
            <Label>Weekend days</Label>
            <div className="flex gap-1.5">
              {(
                [
                  { day: 1, label: "Mon" },
                  { day: 2, label: "Tue" },
                  { day: 3, label: "Wed" },
                  { day: 4, label: "Thu" },
                  { day: 5, label: "Fri" },
                  { day: 6, label: "Sat" },
                  { day: 0, label: "Sun" },
                ] as const
              ).map(({ day, label }) => {
                const active = (formState.response_time.weekend_days ?? []).includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                      active
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-background text-muted-foreground border-input hover:bg-accent"
                    }`}
                    onClick={() =>
                      setFormState((prev) => {
                        const current = prev.response_time.weekend_days ?? [];
                        const next = active
                          ? current.filter((d) => d !== day)
                          : [...current, day];
                        return {
                          ...prev,
                          response_time: { ...prev.response_time, weekend_days: next },
                        };
                      })
                    }
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              Selected days are fully excluded from response time calculation
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Time Savings (ROI) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Time Savings (ROI)</CardTitle>
          <p className="text-xs text-muted-foreground">
            Configure time estimates for calculating agent efficiency
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Full email handling (min)</Label>
              <Input
                type="number"
                min={0}
                value={formState.time_constants.full_handling_min}
                onChange={(e) => updateTimeConstant("full_handling_min", e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Time to handle an email without the agent
              </p>
            </div>
            <div className="space-y-2">
              <Label>Draft review (min)</Label>
              <Input
                type="number"
                min={0}
                value={formState.time_constants.draft_review_min}
                onChange={(e) => updateTimeConstant("draft_review_min", e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Time to review and send a draft
              </p>
            </div>
            <div className="space-y-2">
              <Label>Triage - read &amp; decide (min)</Label>
              <Input
                type="number"
                min={0}
                value={formState.time_constants.triage_min}
                onChange={(e) => updateTimeConstant("triage_min", e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Time to read and decide not to respond
              </p>
            </div>
            <div className="space-y-2">
              <Label>Context saving (min)</Label>
              <Input
                type="number"
                min={0}
                value={formState.time_constants.context_saving_min}
                onChange={(e) => updateTimeConstant("context_saving_min", e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Time saved when the agent pre-gathers context for escalation
              </p>
            </div>
            <div className="space-y-2">
              <Label>Error overhead (min)</Label>
              <Input
                type="number"
                min={0}
                value={formState.time_constants.error_overhead_min}
                onChange={(e) => updateTimeConstant("error_overhead_min", e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Extra time spent when agent makes an error
              </p>
            </div>
            <div className="space-y-2">
              <Label>Working hours/month</Label>
              <Input
                type="number"
                min={1}
                value={formState.time_constants.working_hours_per_month}
                onChange={(e) => updateTimeConstant("working_hours_per_month", e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Used for FTE calculation
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sentiment Thresholds */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Sentiment Thresholds</CardTitle>
          <p className="text-xs text-muted-foreground">
            Score ranges for sentiment classification (0-10 scale). Available after sentiment feature is enabled.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Negative max score</Label>
              <Input
                type="number"
                min={0}
                max={10}
                value={formState.sentiment_thresholds.negative_max}
                onChange={(e) => updateSentimentThreshold("negative_max", e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                <span>0-</span>{formState.sentiment_thresholds.negative_max} <span>= Negative</span>
              </p>
            </div>
            <div className="space-y-2">
              <Label>Neutral max score</Label>
              <Input
                type="number"
                min={0}
                max={10}
                value={formState.sentiment_thresholds.neutral_max}
                onChange={(e) => updateSentimentThreshold("neutral_max", e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {formState.sentiment_thresholds.negative_max + 1}<span>-</span>{formState.sentiment_thresholds.neutral_max} <span>= Neutral,</span> {formState.sentiment_thresholds.neutral_max + 1}<span>-10 = Positive</span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={handleReset}>
          <RotateCcw className="w-4 h-4 mr-2" />
          <span>Reset to Defaults</span>
        </Button>
        <Button
          className="bg-blue-600 hover:bg-blue-700"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          <span>Save Settings</span>
        </Button>
      </div>
    </div>
  );
}
